import React from "react";

import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { useSidebar } from "@replo/design-system/components/sidebar/Sidebar";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { EditorTutorialProvider } from "@v2/features/activation/components/EditorTutorial/EditorTutorialContext";
import { EditorNavigationTutorial } from "@v2/features/activation/components/EditorTutorial/NavigationGuide";
import ManageChats from "@v2/features/chat/components/chat-elements/ManageChats";
import ChatFileDropZone from "@v2/features/chat/components/ChatFileDropZone";
import ReactEditorChat from "@v2/features/chat/components/ReactEditorChat";
import ReactEditorGlobalHotkeysListener from "@v2/features/chat/components/ReactEditorGlobalHotkeysListener";
import { ChatFileDropZoneProvider } from "@v2/features/chat/hooks/useChatFileDropZone";
import { useChatSidebar } from "@v2/features/chat/hooks/useChatSidebar";
import BackendStagingBanner from "@v2/features/debug/BackendStagingBanner";
import DesignLibraryPanel from "@v2/features/design-library/DesignLibraryPanel";
import { ModifierQueueProvider } from "@v2/features/modifiers/store/ModifierQueueStoreContext";
import { ModifiersProvider } from "@v2/features/modifiers/store/ModifiersStoreContext";
import LoadingHeader from "@v2/features/page-renderer/components/LoadingHeader";
import ModifiablePageRenderer from "@v2/features/page-renderer/components/ModifiablePageRenderer";
import PageRenderControls from "@v2/features/page-renderer/components/PageRenderControls";
import { useViewportStore } from "@v2/features/page-renderer/store/viewportStore";
import PageSettingsPanel from "@v2/features/page-settings/PageSettingsPanel";
import VersionHistory from "@v2/features/version-history/VersionHistory";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import {
  useEditorStoreActions,
  useMobileChatMode,
  usePanelView,
} from "@v2/store/editorStore";
import { nextJSProjectRouter } from "@v2/trpc/routes/nextJSProject";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import { useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { z } from "zod";

import MobileChatFooter from "./MobileChatFooter";

/**
 * Listens for changes in the panel view, and if we go to chat, then auto-collapse the sidebar
 * so there's more space for the chat.
 *
 * Note (Noah, 2025-08-30): Kind of weird to have a component for this but it's
 * the only way to respond to chat changes and trigger sidebar changes, since
 * shadcn sidebar only exposes the toggleSidebar through a hook. I didn't do
 * this in the main chat component since that would mean it would rerender
 * unnecessarily when the sidebar changed.
 */
function ChatPanelSidebarListener() {
  const panelView = usePanelView();
  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;
  useOnValueChange(panelView, (newPanelView) => {
    if (newPanelView === "chat" && isSidebarExpanded) {
      toggleSidebar();
    }
  });
  return null;
}

function ReactEditor() {
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");

  const [isUsingBackendStaging] = useLocalStorageState(
    "replo.debug.useBackendStaging",
    false,
    { schema: z.boolean() },
  );

  // Create a ref for the main container to handle keyboard events
  const reactEditorContainerRef = React.useRef<HTMLDivElement>(null);

  const { data: page, isLoading: isPageLoading } =
    nextJSProjectRouter.getPage.useQuery(
      projectId ? { projectId, id: pageId ?? "" } : skipToken,
      {
        enabled: Boolean(projectId && pageId),
      },
    );

  if (!project) {
    return null;
  }

  const isWaitingForPageData = Boolean(pageId && isPageLoading);

  // Tutorial should only show when preview panel is visible (has pageId) and page data is loaded
  const isPreviewPanelReady = Boolean(pageId) && !isWaitingForPageData;

  return (
    <EditorTutorialProvider isEditorReady={isPreviewPanelReady}>
      {isUsingBackendStaging && <BackendStagingBanner />}
      <ReactEditorContent
        pageId={pageId}
        reactEditorContainerRef={reactEditorContainerRef}
        page={page}
        isWaitingForPageData={isWaitingForPageData}
      />
      <ChatPanelSidebarListener />
    </EditorTutorialProvider>
  );
}

function ReactEditorContent({
  pageId,
  reactEditorContainerRef,
  page,
  isWaitingForPageData,
}: {
  pageId: string | null;
  reactEditorContainerRef: React.RefObject<HTMLDivElement>;
  page: any;
  isWaitingForPageData: boolean;
}) {
  const panelView = usePanelView();
  const [searchParams] = useSearchParams();
  const chatIdParam = searchParams.get("chatId");
  const { setPanelView } = useEditorStoreActions();
  const currentViewport = useViewportStore((state) => state.currentViewport);
  const { open: isChatSidebarOpen } = useChatSidebar();

  const isMobile = useIsMobile();

  const mobileChatMode = useMobileChatMode();
  const { setMobileChatMode } = useEditorStoreActions();

  const canPreview = Boolean(pageId);
  const effectiveMobileMode = canPreview ? mobileChatMode : "chat";

  useOnValueChange(chatIdParam, (current, previous) => {
    if (current === "" && previous !== "" && isMobile) {
      setMobileChatMode("chat");
    }
  });

  const getPanelTitle = () => {
    return exhaustiveSwitch({ type: panelView })({
      chat: "Chat",
      history: "Version History",
      designLibrary: "Design Library",
      pageSettings: "Page Settings",
      manageChats: "Manage Chats",
    });
  };

  const panelContent = (
    <>
      {exhaustiveSwitch({ type: panelView })({
        chat: <ReactEditorChat />,
        history: <VersionHistory />,
        designLibrary: <DesignLibraryPanel />,
        pageSettings: page && <PageSettingsPanel page={page} />,
        manageChats: <ManageChats />,
      })}
    </>
  );

  return (
    <ModifiersProvider>
      <ModifierQueueProvider>
        <ChatFileDropZoneProvider>
          <ChatFileDropZone className="h-full w-full">
            {/* NOTE (Kurt, 2025-07-17): We need to create a ref for the main container to handle keyboard events. Without
      this, hotkeys don't work because the events are captured by iframes or other elements.*/}
            <div
              ref={reactEditorContainerRef}
              className="relative flex flex-col md:flex-row md:gap-3 h-full w-full outline-none"
              tabIndex={-1}
            >
              <ReactEditorGlobalHotkeysListener
                containerRef={reactEditorContainerRef}
              />
              <EditorNavigationTutorial />

              <div
                className={twMerge(
                  "hidden md:flex h-full flex-col gap-5 min-h-0 min-w-0 items-stretch self-start",
                  pageId === null && "md:flex-1",
                  pageId && !isChatSidebarOpen && "md:basis-[33%]",
                  pageId && isChatSidebarOpen && "md:basis-[calc(33%+166px)]",
                )}
              >
                {panelContent}
              </div>

              {isMobile && (
                <Drawer
                  open={panelView !== "chat"}
                  onOpenChange={(open) => {
                    if (!open) {
                      setPanelView("chat");
                    }
                  }}
                  title={getPanelTitle()}
                >
                  <>
                    {panelView === "history" && <VersionHistory />}
                    {panelView === "designLibrary" && <DesignLibraryPanel />}
                    {panelView === "pageSettings" && page && (
                      <PageSettingsPanel page={page} />
                    )}
                  </>
                </Drawer>
              )}

              {pageId && (
                <div
                  className={twMerge(
                    "relative min-h-0 flex flex-col items-center w-full shadow-2xs border-0.5 border-border rounded-lg bg-white",
                    "flex-1 flex-shrink-0 md:flex-none md:flex-shrink-0 transition-[flex-basis] duration-300 ease-in-out",
                    !isChatSidebarOpen && "md:basis-[66%]",
                    isChatSidebarOpen && "md:basis-[calc(66%-166px)]",
                    panelView === "chat" &&
                      isMobile &&
                      effectiveMobileMode === "chat" &&
                      "hidden",
                  )}
                >
                  {!isMobile && (
                    <>
                      {pageId && (
                        <PageRenderControls
                          page={page}
                          isLoading={isWaitingForPageData}
                        />
                      )}
                      <div className=" top-0 left-0 w-full relative">
                        <LoadingHeader
                          pageRoute={page?.pageRoute ?? ""}
                          key={pageId}
                        />
                      </div>
                    </>
                  )}

                  <div
                    className="flex-1 relative w-full"
                    style={{
                      maxWidth:
                        currentViewport !== "desktop"
                          ? `${CANVAS_SIZES[currentViewport].defaultFrameWidth}px`
                          : undefined,
                    }}
                  >
                    {page && (
                      <div className="flex flex-col h-full w-full">
                        <ModifiablePageRenderer
                          pageRoute={page.pageRoute ?? ""}
                          className="rounded-t-none w-full rounded-b-lg"
                          isInteractive
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mobile: full-height chat container when in chat mode */}
              {panelView === "chat" &&
                isMobile &&
                effectiveMobileMode === "chat" && (
                  <div className="flex-1 min-h-0 w-full md:hidden overflow-hidden">
                    <ReactEditorChat />
                  </div>
                )}

              {/* Mobile-only footer with Chat/Preview toggle and (in Preview) controls) */}
              {isMobile && (
                <MobileChatFooter
                  pageId={canPreview ? pageId : null}
                  page={page}
                  isLoading={isWaitingForPageData}
                  mode={effectiveMobileMode}
                  onModeChange={(mode) => {
                    if (mode === "preview" && !canPreview) {
                      setMobileChatMode("chat");
                      return;
                    }
                    setMobileChatMode(mode);
                  }}
                />
              )}
            </div>
          </ChatFileDropZone>
        </ChatFileDropZoneProvider>
      </ModifierQueueProvider>
    </ModifiersProvider>
  );
}

export default ReactEditor;
